'use client';

import type { KeyboardEvent } from 'react';
import type { WelcomeMissionKind } from './welcomeMission.consts';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useId } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';
import { formatCompactNumber } from '@/utils/currency.utils';
import { welcomeMissionImages } from './welcomeMission.consts';
import { isMissionCompleted } from './welcomeMission.utils';

interface WelcomeMissionCardProps {
  kind: WelcomeMissionKind;
  mission: UserQuestDto;
  onAction?: VoidFunction;
  actionDisabled?: boolean;
  completedOverride?: boolean;
  locked?: boolean;
}

export const WelcomeMissionCard = ({ kind, mission, onAction, actionDisabled, completedOverride, locked = false }: WelcomeMissionCardProps) => {
  const t = useTranslations('gamification.mission-group');
  const img = welcomeMissionImages[kind];
  const completed = completedOverride ?? isMissionCompleted(mission);
  const xp = mission.quest.xpAmount ?? mission.quest.questTemplate.xpAmount ?? 0;
  const interactive = !!onAction && !completed && !actionDisabled && !locked;
  const lockGradientId = `welcome-missions-lock-gradient-${useId().replace(/:/g, '')}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    onAction();
  };

  return (
    <div
      {...(
        interactive
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: onAction,
              onKeyDown: handleKeyDown,
            }
          : {}
      )}
      className={cn(
        'relative shadow-card min-w-46 w-full glass-stroke-border gm-welcome-mission-gradient lg:h-24 h-18 rounded-2xl',
        interactive && 'cursor-pointer',
        completed && 'opacity-50',
      )}
    >
      {locked && (
        <>
          <svg width={0} height={0} className="absolute" aria-hidden>
            <defs>
              <linearGradient id={lockGradientId} x1="100%" y1="11%" x2="5%" y2="9%">
                <stop offset="33%" stopColor="#F2DBFF" />
                <stop offset="100%" stopColor="#FE5104" />
              </linearGradient>
            </defs>
          </svg>
          <div className="flex justify-center absolute inset-0 z-2 bg-black/40 items-center rounded-2xl">
            <Icon
              name="block"
              size={24}
              gradientFill={`url(#${lockGradientId})`}
            />
          </div>
        </>
      )}
      <div className={cn(
        'flex relative h-full justify-start max-lg:pl-2 lg:justify-center items-center gap-2',
        locked && 'blur-xs',
      )}
      >
        <Image
          src={img.src}
          width={img.width}
          height={img.height}
          alt={mission.quest.name}
          className="w-16 h-14"
        />
        <div className="flex flex-col gap-1">
          <p className="text-sm md:text-lg font-medium text-light leading-5">
            {mission.quest.name}
          </p>
          <p className="md:text-base text-xs tracking-tighter leading-4 gm-mission-xp-text font-normal">
            {completed ? t('completed') : `${formatCompactNumber(xp, { minUnit: 'thousand' })} XP`}
          </p>
        </div>
      </div>
    </div>
  );
};
